import isEmpty from 'lodash/isEmpty';
import { createSelector } from 'reselect';

import { AppState } from 'src/config/appState';

import { ProgramEnum } from '../../models/programs';
import {
    AnswersBySurveysIds,
    GenericSurveyAnswer,
    GenericSurveyQuestion,
    ProgramSurvey,
    ProgramSurveys,
} from '../models/surveys';
import { GenericProgramSurvey } from '../models/surveysGeneric';
import { isAnyAnswerValid, isEveryAnswerValid } from './surveysAnswersValidators';

export const extractSurveyId = (
    _state: AppState,
    _programId: ProgramEnum,
    surveyId: GenericProgramSurvey,
) => surveyId;

export const extractSeveralSurveyIds = (
    _state: AppState,
    _programId: ProgramEnum,
    surveysIds: GenericProgramSurvey[],
) => surveysIds;

export const extractQuestionId = (
    _state: AppState,
    _programId: ProgramEnum,
    _surveyId: GenericProgramSurvey,
    id: number,
) => id;

export const extractQuestionRenderId = (
    _state: AppState,
    _programId: ProgramEnum,
    _surveyId: GenericProgramSurvey,
    renderId: string,
) => renderId;

export const extractProgramId = (_state: AppState, id: ProgramEnum) => id;

export const getSurveys = (state: AppState) => state.surveys;

export const getProgramsSurveys = (state: AppState) => getSurveys(state).programsSurveys;

export const getSingleProgramSurveys = createSelector(
    getProgramsSurveys,
    extractProgramId,
    (programsSurveys, programId): ProgramSurveys | undefined =>
        programsSurveys && programsSurveys[programId],
);

export const getProgramSingleSurvey = createSelector(
    getSingleProgramSurveys,
    extractSurveyId,
    (programSurveys, surveyId): ProgramSurvey | undefined =>
        programSurveys && programSurveys[surveyId],
);

export const getProgramSurveyQuestions = createSelector(
    getProgramSingleSurvey,
    (survey): GenericSurveyQuestion[] | undefined => survey && survey.questions,
);

export const getProgramSurveyAnswers = createSelector(
    getProgramSingleSurvey,
    (survey): GenericSurveyAnswer[] | undefined => survey && survey.answers,
);

export const getProgramSurveyQuestionByRenderId = createSelector(
    getProgramSurveyQuestions,
    extractQuestionRenderId,
    (questions = [], renderId): GenericSurveyQuestion | undefined =>
        questions.find(question => question.renderId === renderId),
);

export const getProgramSurveyQuestionAnswerByRenderId = createSelector(
    getProgramSurveyAnswers,
    extractQuestionRenderId,
    (answers = [], renderId): GenericSurveyAnswer | undefined =>
        answers.find(answer => answer.renderId === renderId),
);

export const getProgramSurveySingleQuestionAnswer = createSelector(
    getProgramSurveyAnswers,
    extractQuestionId,
    (answers = [], questionId): GenericSurveyAnswer | undefined =>
        answers.find(answer => answer.question === questionId),
);

export const getSurveysAnswersBySurveysIds = createSelector(
    getSingleProgramSurveys,
    extractSeveralSurveyIds,
    (programSurveys, surveysIds): AnswersBySurveysIds | undefined => {
        if (!programSurveys || !surveysIds || isEmpty(surveysIds)) {
            return;
        }

        return surveysIds.reduce((acc, surveyId) => {
            const survey = programSurveys[surveyId];

            return survey
                ? {
                      ...acc,
                      [surveyId]: survey.answers,
                  }
                : acc;
        }, {});
    },
);

export const isProgramSurveyAnswered = createSelector(getProgramSurveyAnswers, isEveryAnswerValid);

export const isProgramSurveyPartiallyAnswered = createSelector(
    getProgramSurveyAnswers,
    isAnyAnswerValid,
);

export const isSeveralProgramSurveysDataValid = createSelector(
    getSurveysAnswersBySurveysIds,
    (surveysAnswersBySurveysIds): boolean => {
        if (!surveysAnswersBySurveysIds || isEmpty(surveysAnswersBySurveysIds)) {
            return false;
        }

        return Object.keys(surveysAnswersBySurveysIds).reduce(
            (acc, surveyId) =>
                acc === true && isEveryAnswerValid(surveysAnswersBySurveysIds[surveyId])
                    ? true
                    : false,
            true,
        );
    },
);

const countValidSurveys = (
    surveysAnswersBySurveysIds: AnswersBySurveysIds | undefined,
    validationFunc: (answers: GenericSurveyAnswer[]) => boolean,
): number =>
    surveysAnswersBySurveysIds
        ? Object.values(surveysAnswersBySurveysIds).reduce(
              (acc, answers) => (answers && validationFunc(answers) ? acc + 1 : acc),
              0,
          )
        : 0;

export const getAnsweredSurveysAmount = createSelector(
    getSurveysAnswersBySurveysIds,
    (surveysAnswersBySurveysIds): number =>
        countValidSurveys(surveysAnswersBySurveysIds, isEveryAnswerValid),
);

export const getPartiallyAnsweredSurveysAmount = createSelector(
    getSurveysAnswersBySurveysIds,
    (surveysAnswersBySurveysIds): number =>
        countValidSurveys(surveysAnswersBySurveysIds, isAnyAnswerValid),
);

export const areCertainProgramSurveysAnswered = createSelector(
    getAnsweredSurveysAmount,
    extractSeveralSurveyIds,
    (answeredSurveysAmount, surveysIds): boolean => answeredSurveysAmount === surveysIds.length,
);
